import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useApi } from './use-api';
import { queryKeys } from './use-data';

import { dataService } from '@/services/data-service';
import { UserRole, type WorkOrder } from '@/types/models';
import { approvalSlaStatus, type ApprovalSlaStatus } from '@/utils/approval-sla';

/**
 * THE work-order queues for the signed-in user — one query per queue, one number,
 * everywhere (#496).
 *
 * ── WHY THIS EXISTS ──
 *
 * It replaces `usePendingApprovals(pmId)`, whose single argument had come to mean three
 * different things, so every caller invented its own convention and every surface showed a
 * different number for the same concept:
 *
 *   the stat tile + sidebar badge  passed `undefined`  → portfolio-wide      → 280
 *   the To Do panel                passed `user.id`    → scoped to the user  →  86
 *   the Needs Approval page        passed `undefined`  → `enabled: !!pmId`   →   0
 *                                                        DISABLED the query entirely
 *
 * The third was the worst: a missing scope silently meant "do not run", so the page that
 * exists to show the queue showed an empty one. Nothing errored.
 *
 * So the scope decision is made HERE, once, and consumers get no say. They read `count`,
 * `rows` and `params` — and because `params` is what the page passes to its own list, the
 * badge, the tile and the table cannot drift apart.
 *
 * ── TWO QUEUES, TWO ROUTES ──
 *
 * `useWorkOrderQueue(kind)` is the general form: ask for a queue by name and get it scoped
 * for the viewer. The sidebar's Needs Review and Needs Approval rows, the two route pages
 * and the dashboard tiles use it, so an admin can hold both without the hook choosing.
 *
 * `useApprovalQueue()` is the viewer's PRIMARY queue — the one that feeds the To Do panel,
 * the overdue banner and the interstitial, where offering two lists of work would make
 * neither feel owned. Accounting's is Needs Review; everyone else's is Needs Approval.
 */

/**
 * How many rows the queue fetches.
 *
 * Was 200, which was BELOW the real depth of accounting's review queue (272 on the day this
 * was raised). That made `count` — the server total — disagree with every surface that
 * counts the fetched rows instead, which is the same class of mismatch this hook exists to
 * prevent, just relocated: the tile said 272 while the To Do badge beside it said 200.
 *
 * 500 clears the real backlog with headroom. It is still a CEILING, not a guarantee, so
 * `truncated` is exposed rather than left implicit — a consumer that counts rows must be
 * able to tell when it is counting a page instead of the whole queue.
 */
export const QUEUE_FETCH_LIMIT = 500;

export type ApprovalQueueKind = 'needs_review' | 'pending_approval';

export interface ApprovalQueueRow {
	workOrder: WorkOrder;
	/** Null on the needs-review queue: the SLA clock only starts once it is handed over. */
	sla: ApprovalSlaStatus | null;
}

export interface ApprovalQueue {
	kind: ApprovalQueueKind;
	/** Card/tile/page heading. One string, so the three cannot disagree either. */
	label: string;
	/** What the tile's sub-label should say. */
	caption: string;
	rows: ApprovalQueueRow[];
	/**
	 * Just the rows past their SLA — always a subset of `rows`, never a second query.
	 *
	 * Exposed here rather than left to each consumer to filter, because the banner, the
	 * interstitial and the To Do panel all need it and `sla?.overdue` is exactly the sort of
	 * one-liner that gets written three slightly different ways.
	 *
	 * Empty on the needs-review queue by construction: those rows carry no `sla`.
	 */
	overdue: ApprovalQueueRow[];
	count: number;
	/**
	 * How many of `rows` are overdue.
	 *
	 * Counted from the fetched page, so it saturates at the fetch cap — unlike `count`,
	 * which is the server total. Under-reporting a huge overdue pile is the safe direction:
	 * it can only ever be lower than reality, never invented.
	 */
	overdueCount: number;
	/**
	 * True when the server total exceeds what was fetched.
	 *
	 * `count` stays authoritative; this says "the rows are a page". Anything deriving a
	 * number from `rows.length` is under-reporting whenever this is set.
	 */
	truncated: boolean;
	/**
	 * The EXACT query params behind `count`.
	 *
	 * Handed out so a consumer rendering its own list (the table page, a WorkOrdersList)
	 * uses the same filter the number came from, rather than reconstructing it and getting
	 * it subtly wrong.
	 */
	params: { reviewState: ApprovalQueueKind; associationPmId?: string; includeUnassigned?: boolean };
	isLoading: boolean;
	refetch: () => Promise<unknown>;
}

const QUEUE_LABEL: Record<ApprovalQueueKind, string> = {
	needs_review: 'Needs Review',
	pending_approval: 'Needs Approval',
};

const QUEUE_CAPTION: Record<ApprovalQueueKind, string> = {
	needs_review: 'completed, not yet sent for approval',
	pending_approval: 'waiting on your approval',
};

/**
 * Does this user do the accounting review pass?
 *
 * THE ACCOUNTING ROLE ONLY — deliberately not `isAdmin`.
 *
 * Admins hold `REVIEW_WORK_ORDERS`, so an earlier version keyed on the permission and gave
 * every admin the review queue as their PRIMARY queue. That was wrong in practice: an admin
 * who owns associations is acting as a property manager, and routing them to Needs Review
 * hid the approvals they personally owed. Being *able* to review is not the same as review
 * being your job. An admin who does review reaches Needs Review from its own sidebar row.
 */
function isReviewer(user: { role?: string } | null | undefined): boolean {
	return user?.role === UserRole.Accounting;
}

/**
 * Whose approvals the pending-approval queue holds.
 *
 * Everyone gets their OWN associations, except an ADMIN *browsing* the queue, who gets the
 * whole portfolio: they are the escalation point for every manager, and the page offers a
 * PM filter to narrow it.
 *
 * ── `personal` IS THE BANNER FIX ──
 *
 * That admin exception is right for a page you chose to open and wrong for anything that
 * INTERRUPTS you. `useApprovalQueue` — the overdue banner, the blocking interstitial, the
 * To Do panel — passes `personal: true`, so those surfaces only ever speak about work the
 * viewer actually owes.
 *
 * Without it, an admin who manages no associations got a permanent red "1 work order is
 * overdue for your approval" bar on every page of the app, about somebody else's work
 * order, and no action available to them could clear it. Breadth is a feature on a page and
 * a defect in an alarm.
 *
 * Note this is NOT keyed on `role === PropertyManager`. Association ownership is what
 * matters — `associations.assigned_to` — and a support or maintenance user who happens to
 * own one should see it. Keying on the role was how the tile ended up portfolio-wide for
 * an admin while the To Do panel beside it was scoped.
 */
function approvalScopePmId(
	user: { id?: string; role?: string } | null | undefined,
	personal: boolean,
): string | undefined {
	if (!user?.id) return undefined;
	if (personal) return user.id;
	return user.role === UserRole.Admin ? undefined : user.id;
}

/**
 * Who can approve a work order on an association with NOBODY assigned.
 *
 * Mirrors `mayApproveWorkOrder` (api/work-order-review.ts) deliberately — the Admin ROLE
 * alone. The retired `is_admin` column is NOT consulted: an admin-flagged property manager
 * approves her own associations and nothing else, and that is already expressed by the role.
 *
 * Such a row matches no `associationPmId` at all, so under personal scope it would vanish
 * from every alarm while remaining approvable ONLY by these users. They are told about it
 * precisely because they are the only ones who can clear it — the same rule that keeps the
 * banner off everyone else, applied in the other direction.
 *
 * THE ALARM'S REACH MUST EQUAL THE ACTION RIGHT. While this demanded the column that
 * `api/users.ts` now defaults to 0, every NEW administrator was the only person able to
 * approve an unassigned association's work orders and the only person never told one was
 * waiting — a silent omission, the failure mode nothing surfaces.
 */
function isApproverOfLastResort(user: { role?: string } | null | undefined): boolean {
	return user?.role === UserRole.Admin;
}

export interface UseWorkOrderQueueOptions {
	/** Pin "now" for tests and for the daily SLA bucket. */
	atMs?: number;
	/**
	 * Skip the fetch entirely — for a sidebar row the viewer cannot see. Defaults to true.
	 * Never derived from scope: a missing scope is legitimate (see `useApprovalQueue`).
	 */
	enabled?: boolean;
	/**
	 * "Only work I personally owe" — drops the admin's portfolio-wide exception.
	 *
	 * Set by `useApprovalQueue` and by nothing else. A surface that INTERRUPTS the viewer
	 * must not be able to fire on another manager's backlog; a surface they navigated to
	 * deliberately should keep the admin's full breadth. Defaults to false so the browsing
	 * consumers (tile, sidebar badge, queue page) are unaffected.
	 */
	personal?: boolean;
}

/** A named queue, scoped for the viewer. */
export function useWorkOrderQueue(kind: ApprovalQueueKind, options: UseWorkOrderQueueOptions = {}): ApprovalQueue {
	const { user } = useApi();
	const { atMs, enabled = true, personal = false } = options;

	// A reviewer's queue is portfolio-wide by definition: nothing has been handed to anyone yet.
	const associationPmId = kind === 'needs_review' ? undefined : approvalScopePmId(user, personal);
	/**
	 * Only meaningful alongside a scope — an unscoped query already spans every association,
	 * orphaned ones included, so sending it there would be a redundant param that could
	 * only drift from the one that matters.
	 */
	const includeUnassigned = !!associationPmId && personal && isApproverOfLastResort(user);

	const params = useMemo(
		() => ({
			reviewState: kind,
			...(associationPmId ? { associationPmId } : {}),
			...(includeUnassigned ? { includeUnassigned: true as const } : {}),
		}),
		[kind, associationPmId, includeUnassigned],
	);

	const query = useQuery({
		queryKey: queryKeys.workOrders.list({ ...params, limit: QUEUE_FETCH_LIMIT }),
		queryFn: () => dataService.getWorkOrders({ ...params, limit: QUEUE_FETCH_LIMIT }),
		/**
		 * Gated on having a USER (and the caller's opt-out), never on having a scope.
		 *
		 * `enabled: !!associationPmId` is the bug this hook was written to kill: for a
		 * reviewer or an admin the scope is legitimately absent, and treating that as "do
		 * not run" turned the whole queue into an empty list.
		 */
		enabled: enabled && !!user?.id,
	});

	// Pinned so the memo below has a stable dependency; the SLA answer only changes when
	// the Eastern calendar day rolls over.
	const nowMs = atMs ?? Date.now();
	const dayBucket = Math.floor(nowMs / 86_400_000);

	const rows = useMemo<ApprovalQueueRow[]>(() => {
		const items = (query.data?.data ?? []) as WorkOrder[];
		return items.map((wo) => ({
			workOrder: wo,
			// Only the pending-approval queue has a clock running.
			sla: kind === 'pending_approval' && wo.reviewApprovedAt
				? approvalSlaStatus(Number(wo.reviewApprovedAt), nowMs)
				: null,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query.data, kind, dayBucket]);

	const overdue = useMemo(() => rows.filter((r) => r.sla?.overdue), [rows]);

	return {
		kind,
		label: QUEUE_LABEL[kind],
		caption: QUEUE_CAPTION[kind],
		rows,
		overdue,
		// The TOTAL, not `rows.length` — the query is capped, and a count that silently
		// maxed out would be worse than one that is occasionally larger than the list
		// beneath it.
		count: query.data?.total ?? rows.length,
		overdueCount: overdue.length,
		truncated: (query.data?.total ?? rows.length) > rows.length,
		params,
		isLoading: query.isLoading,
		refetch: query.refetch,
	};
}

/**
 * The viewer's PRIMARY queue — what the To Do panel, the overdue banner and the
 * interstitial show. One queue per person: accounting's is Needs Review, everyone else's
 * is Needs Approval. Somebody who can do both jobs still has one queue that is *theirs*.
 *
 * `personal: true` is the whole difference from `useWorkOrderQueue`, and it is not
 * optional: every consumer of this hook either interrupts the viewer or claims to list
 * their own work, so *theirs* has to mean theirs for an admin as well.
 */
export function useApprovalQueue(atMs?: number): ApprovalQueue {
	const { user } = useApi();
	const kind: ApprovalQueueKind = isReviewer(user) ? 'needs_review' : 'pending_approval';
	return useWorkOrderQueue(kind, { atMs, personal: true });
}
